import React from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { slugify } from "../../utils/slugify"
import "./stepper.scss"

const STEPS = [
  { id: 1, label: "Search", icon: "search" },
  { id: 2, label: "Choose Cab", icon: "cab" },
  { id: 3, label: "Review", icon: "review" },
  { id: 4, label: "Confirmed", icon: "confirmed" },
]

const STEP_ICONS = {
  search: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  cab: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 17h14M5 17a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1l2-3h8l2 3h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2M5 17v2m14-2v2" />
      <circle cx="7.5" cy="17" r="0.5" /><circle cx="16.5" cy="17" r="0.5" />
    </svg>
  ),
  review: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  confirmed: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
}

const getStepPath = (stepId) => {
  const tripData = JSON.parse(localStorage.getItem("tripData") || "{}")
  switch (stepId) {
    case 1:
      return "/"
    case 2: {
      if (tripData.trip_type === 3 && tripData.pickup_city && tripData.drop_city) {
        const from = slugify(tripData.pickup_city)
        const to = slugify(tripData.drop_city)
        return `/routes/${from}-to-${to}-cab/`
      }
      return "/cabs"
    }
    case 3:
      return "/bookCab"
    case 4:
      return null // No dedicated page
    default:
      return "/"
  }
}

const ProgressStepper = ({ currentStep }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const handleStepClick = (stepId) => {
    // Only allow clicking completed steps (before current)
    if (stepId >= currentStep) return
    // Step 4 has no page
    if (stepId === 4) return

    const path = getStepPath(stepId)
    if (path) {
      navigate(path)
    }
  }

  return (
    <div className="progress-stepper">
      <div className="progress-stepper__track">
        {STEPS.map((step, index) => {
          const isCompleted = step.id < currentStep
          const isCurrent = step.id === currentStep
          const isUpcoming = step.id > currentStep
          const isClickable = isCompleted && step.id !== 4

          return (
            <React.Fragment key={step.id}>
              {/* Connector line */}
              {index > 0 && (
                <div
                  className={`progress-stepper__connector ${
                    isCompleted || isCurrent ? "progress-stepper__connector--filled" : ""
                  }`}
                />
              )}

              {/* Step node */}
              <div
                className={`progress-stepper__step ${
                  isCompleted ? "progress-stepper__step--completed" : ""
                } ${isCurrent ? "progress-stepper__step--current" : ""} ${
                  isUpcoming ? "progress-stepper__step--upcoming" : ""
                } ${isClickable ? "progress-stepper__step--clickable" : ""}`}
                onClick={() => handleStepClick(step.id)}
                role={isClickable ? "button" : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onKeyDown={isClickable ? (e) => e.key === "Enter" && handleStepClick(step.id) : undefined}
                aria-label={`Step ${step.id}: ${step.label}${isCompleted ? " (completed)" : ""}${isCurrent ? " (current)" : ""}`}
              >
                <div className="progress-stepper__circle">
                  {isCompleted ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    STEP_ICONS[step.icon]
                  )}
                </div>
                <span className="progress-stepper__label">{step.label}</span>
              </div>
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

export default ProgressStepper
